/**
 * features/notifications/hooks/useNotifications.ts
 *
 * Centro interno (N1). Lee las notificaciones PROPIAS por RPC server-authoritative
 * (list_my_notifications) y marca leído (mark_notification_read / mark_all_notifications_read). No hay
 * insert desde el navegador. Refresca por polling ligero (el realtime llega en un slice posterior).
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@lib/supabase/client'

export type NotificationPriority = 'INFO' | 'ACTION_REQUIRED' | 'URGENT'

export interface NotificationItem {
  id: string
  event_type: string
  source_module: string
  entity_type: string | null
  entity_id: string | null
  priority: NotificationPriority
  title: string
  body: string | null
  payload_safe: Record<string, unknown>
  created_at: string
  read_at: string | null
}

export interface NotificationsView {
  items: NotificationItem[]
  unread: number
  loading: boolean
  error: string | null
  markRead: (id: string) => Promise<void>
  markAll: () => Promise<void>
  refresh: () => Promise<void>
}

export function useNotifications(pollMs = 60000): NotificationsView {
  const [items, setItems] = useState<NotificationItem[]>([])
  const [unread, setUnread] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const { data, error: e } = await supabase.rpc('list_my_notifications', { p_limit: 30 })
    if (e) {
      setError('No se pudieron cargar las notificaciones.')
      setLoading(false)
      return
    }
    const d = (data ?? {}) as { items?: NotificationItem[]; unread?: number }
    setItems(d.items ?? [])
    setUnread(d.unread ?? 0)
    setError(null)
    setLoading(false)
  }, [])

  useEffect(() => {
    void load()
    if (pollMs <= 0) return
    const t = setInterval(() => void load(), pollMs)
    return () => clearInterval(t)
  }, [load, pollMs])

  const markRead = useCallback(
    async (id: string) => {
      await supabase.rpc('mark_notification_read', { p_id: id })
      await load()
    },
    [load]
  )

  const markAll = useCallback(async () => {
    await supabase.rpc('mark_all_notifications_read', {})
    await load()
  }, [load])

  return { items, unread, loading, error, markRead, markAll, refresh: load }
}
